import type { GuestCheckoutLimitUpgradeStatus } from "@moonpay/platform-protocol/frames/challenge";
import { track } from "@connect/observability";
import type { PostMessageClient } from "@connect/post-message/client";
import type { ChallengeMessageMap } from "@connect/post-message/protocol";
import { flushTerminal } from "../../../../lib/flush-terminal.ts";
import { trackFrameReady } from "../../../../lib/track-frame-ready.ts";
import type { MessageMeta } from "../../../../lib/external-messages.types.ts";

const FLOW = "guest_checkout_limit_upgrade" as const;

export function createExternalMessages(send: PostMessageClient<ChallengeMessageMap>["send"]) {
  const fail = async (payload: ChallengeMessageMap["error"], meta: MessageMeta) => {
    track.info("connect.challenge.failed", { flow: FLOW, code: payload.code, ...meta });
    await flushTerminal();
    send("error", payload);
  };

  return {
    ready() {
      track.info("connect.challenge.ready", { flow: FLOW });
      trackFrameReady(FLOW);
      send("ready");
    },

    async complete(status: GuestCheckoutLimitUpgradeStatus) {
      track.info("connect.challenge.succeeded", { flow: FLOW, status });
      await flushTerminal();
      send("complete", { flow: FLOW, status });
    },

    async cancelled() {
      track.info("connect.challenge.cancelled", { flow: FLOW });
      await flushTerminal();
      send("cancelled", { flow: FLOW });
    },

    invalidToken() {
      return fail(
        {
          code: "generic",
          message: "Invalid or unsupported challengeToken",
        },
        { reason: "invalid_or_unsupported_token" },
      );
    },

    notEligible() {
      return fail(
        {
          code: "generic",
          message: "Guest checkout limit upgrade not eligible",
        },
        { reason: "not_eligible" },
      );
    },

    verificationIncomplete() {
      return fail(
        {
          code: "generic",
          message:
            "Guest checkout limit upgrade requires additional verification that this challenge does not support",
        },
        { reason: "verification_incomplete" },
      );
    },

    error(payload: ChallengeMessageMap["error"], meta: MessageMeta = {}) {
      return fail(payload, meta);
    },
  };
}
